<script>

  import { External, M } from '../../external';
  import { onDestroy, onMount } from 'svelte';
  import { makeWebsiteRequest } from '../../Common/tools';
  import { fade } from 'svelte/transition';

  export let amount, futureAmount, currency, settings;
  let cardInstance, braintree3DS2Instance, paypalInstance, hasPayPal;

  let connectedPayPal = false;

  let paypalData = {
    cardType: '',
    nonce: '',
    paypalEmail: '',
    paypalCountry: ''
  };

  export const onAmountChange = () => {
  };

  export const getPaymentDetails = async (billingInfo) => {
    if (connectedPayPal) {
      return paypalData;
    }
    return await getCardDetails(billingInfo);
  };

  let getCardDetails = (billingInfo) => {
    return new Promise((resolve, reject) => {
      if (!cardInstance) {
        resolve({});
      } else {
        cardInstance.tokenize((tokenizeErr, payload) => {
          if (tokenizeErr) {
            let error = null;
            switch (tokenizeErr.code) {
              case 'HOSTED_FIELDS_FIELDS_EMPTY':
              case 'HOSTED_FIELDS_FIELDS_INVALID':
                error = M.ERR_BRAINTREE_EMPTY_CARD_FIELDS;
                break;
              case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
                error = M.ERR_BRAINTREE_GENERAL;
                break;
              case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
                error = M.ERR_BRAINTREE_NETWORK_ERROR;
                break;
              default:
                error = M.ERR_BRAINTREE_UNKNOWN;
            }
            External.CPanel.showError(error);
            reject(error);
            return;
          }

          let nameParts = (billingInfo.name || '').split(' ');
          let btBillingAddress = {};

          if (nameParts[0].trim().length > 0)
            btBillingAddress.givenName = External.Tools.removeDiacritics(nameParts[0].trim(), true);
          nameParts.splice(0, 1);
          let surname = nameParts.join(' ').trim();
          if (surname)
            btBillingAddress.surname = External.Tools.removeDiacritics(surname, true);

          if (billingInfo.address.trim())
            btBillingAddress.address = billingInfo.address.trim();

          if ((billingInfo.city || '').trim())
            btBillingAddress.locality = billingInfo.city.trim();

          if ((billingInfo.state || '').trim())
            btBillingAddress.region = billingInfo.state.trim();

          if ((billingInfo.zip || '').trim())
            btBillingAddress.postalCode = billingInfo.zip.trim();

          if (billingInfo.countryCode.trim())
            btBillingAddress.countryCodeAlpha2 = billingInfo.countryCode.trim();
          if (braintree3DS2Instance) {
            braintree3DS2Instance.verifyCard({
              challengeRequested: true,
              onLookupComplete: (data, next) => {
                next();
              },
              amount: External.WebPlatform.currToStr(amount, '', true, currency),
              nonce: payload.nonce,
              bin: payload.details.bin,
              btBillingAddress: btBillingAddress,
              email: External.userEmail
            }, (verifyError, payload) => {
              if (verifyError) {
                External.Log.log(verifyError);
                let extra = verifyError.details?.originalError?.description || '';
                let error = verifyError.message + (extra ? ' - ' + extra : '');
                External.CPanel.showError(error);
                reject(error);
                return;
              }

              External.Log.log('liabilityShiftPossible: ' + payload.liabilityShiftPossible);
              External.Log.log('liabilityShifted: ' + payload.liabilityShifted);
              // success if authentication was successful or the card doesn't support it
              if (payload.liabilityShifted || !payload.liabilityShiftPossible) {
                resolve({
                  nonce: payload.nonce,
                  cardType: payload.details.cardType,
                  lastTwo: payload.details.lastTwo
                });
              } else {
                External.CPanel.showError('Card authorization failed. Please try again');
                reject();
              }
            });
          } else { // just in case 3ds wasn't enabled
            resolve({
              nonce: payload.nonce,
              cardType: payload.details.cardType,
              lastTwo: payload.details.lastTwo
            });
          }
        });
      }
    });
  };

  let setup = async () => {
    let result = await makeWebsiteRequest({
      action: 'getClientToken'
    });

    window.braintree.client.create({
      authorization: result.data
    }, (err, clientInstance) => {
      if (err) {
        alert(err);
      } else {
        window.braintree.hostedFields.create({
          client: clientInstance,
          styles: {
            input: {
              color: '#898989',
              'font-family': 'Arial, sans-serif'
            }
          },
          fields: {
            number: {
              selector: '[data-name=\'card-number\']',
              placeholder: M.CARD_NUMBER
            },
            cvv: {
              selector: '[data-name=\'card-cvv\']',
              placeholder: M.CVV
            },
            expirationDate: {
              selector: '[data-name=\'card-expiration\']',
              placeholder: M.MM_YYYY
            }
          }
        }, (hostedFieldsErr, hostedFieldsInstance) => {
          if (hostedFieldsErr) {
            External.CPanel.showError(hostedFieldsErr.message);
            External.Log.error(hostedFieldsErr);
            return;
          }

          External.WebPlatform.initiateBraintree3DSecureIfEnabled(clientInstance, (threeDSecureInstance) => {
            cardInstance = hostedFieldsInstance;
            braintree3DS2Instance = threeDSecureInstance;
          }, (err) => {
            External.CPanel.showError(err.message);
            External.Log.error(err);
          });
        });

        if (hasPayPal) {
          window.braintree.paypal.create({
            client: clientInstance
          }, (paypalErr, instance) => {
            if (paypalErr) {
              External.CPanel.showError(paypalErr);
              External.Log.error(paypalErr);
            }
            paypalInstance = instance;
          });
        }
      }
    });
  };

  let disconnectPayPal = () => {
    paypalData.cardType = '';
    paypalData.nonce = '';
    paypalData.paypalEmail = '';
    paypalData.paypalCountry = '';
    connectedPayPal = false;
  };

  let connectPayPal = () => {
    if (!paypalInstance) {
      return;
    }

    paypalInstance.tokenize({
      flow: 'vault'
    }, (tokenizeErr, payload) => {
      if (tokenizeErr) {
        if (tokenizeErr.type !== 'CUSTOMER') {
          External.Log.error(tokenizeErr);
          External.CPanel.showError(tokenizeErr.message || M.ERR_BRAINTREE_UNKNOWN);
        }
        return;
      }

      paypalData.cardType = 'PayPal';
      paypalData.nonce = payload.nonce;
      paypalData.paypalEmail = payload.details.email;
      paypalData.paypalCountry = payload.details.countryCode;
      connectedPayPal = true;
    });
  };

  onMount(() => {
    hasPayPal = !window.paypalDisabled;

    External.WebPlatform.requestScripts([{
      script: 'https://js.braintreegateway.com/web/3.115.2/js/client.min.js',
      check: () => window.braintree?.client
    }, {
      script: 'https://js.braintreegateway.com/web/3.115.2/js/three-d-secure.min.js',
      check: () => window.braintree?.threeDSecure
    }, {
      script: 'https://js.braintreegateway.com/web/3.115.2/js/hosted-fields.min.js',
      check: () => window.braintree?.hostedFields
    }, {
      script: 'https://js.braintreegateway.com/web/3.115.2/js/paypal.min.js',
      check: () => window.braintree?.paypal
    }], setup);
  });

  let teardown = () => {
    return new Promise((resolve, reject) => {
      try {
        if (cardInstance) {
          cardInstance.teardown(() => {
            cardInstance = null;
            if (!paypalInstance) {
              resolve();
            }
          });
          braintree3DS2Instance = null;
          if (paypalInstance) {
            paypalInstance.teardown(() => {
              paypalInstance = null;
              if (!cardInstance) {
                resolve();
              }
            });
          }
        }
      } catch (error) {
        resolve();
      }
    });
  };

  onDestroy(teardown);
</script>

<div class="payment-info">
  {#if connectedPayPal}
    <div class="col20 f-left connected-paypal-account" in:fade>
      <h4 style="margin-bottom:15px;">{M.CRED_CARD_INFO}</h4>
      <span class="paypal-mail col20 f-left">
        <span>{paypalData.paypalEmail}</span>
        <a on:click={disconnectPayPal}>&#10005;</a>
      </span>
      <span class="col20 f-left light-font">{M.SWITCH_PAYMENTS}</span>
    </div>
  {/if}

  <div class="col20 f-left card-form" class:none={connectedPayPal} in:fade>
    <h4 style="margin-bottom:15px;">{M.CRED_CARD_INFO}</h4>
    <div data-name="card-number" class="input-field"><img src="{External.resources}/images/bt-paymentcards.png" class="bt-paymentcards"></div>
    <div class="col20 f-left">
      <div class="card-dates">
        <div data-name="card-expiration" class="input-field"></div>
        <div data-name="card-cvv" class="input-field"></div>
      </div>

      {#if hasPayPal}
        <a id="paypalContainer" on:click={connectPayPal}>
          <span>{M.OR_CONNECT}</span>
          <img src="https://www.paypalobjects.com/webstatic/en_US/i/btn/png/silver-rect-paypal-44px.png" alt="PayPal">
        </a>
      {/if}
    </div>
  </div>
</div>
